import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, CheckCircle, Clock, AlertTriangle, Cpu, FileCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  const metricCards = [
    {
      title: 'Total Tickets',
      value: metrics?.total_tickets || 0,
      icon: Activity,
      description: 'All customization requests',
      color: 'text-blue-600'
    },
    {
      title: 'Active Tickets',
      value: metrics?.active_tickets || 0,
      icon: Clock,
      description: 'Currently in progress',
      color: 'text-amber-600'
    },
    {
      title: 'Completed',
      value: metrics?.completed_tickets || 0,
      icon: CheckCircle,
      description: 'Successfully delivered',
      color: 'text-green-600'
    },
    {
      title: 'Active Agents',
      value: metrics?.active_agents || 0,
      icon: Cpu,
      description: 'AI agents working',
      color: 'text-purple-600'
    },
    {
      title: 'Pending Approvals',
      value: metrics?.pending_approvals || 0,
      icon: AlertTriangle,
      description: 'Awaiting governance review',
      color: 'text-orange-600'
    },
    {
      title: 'Clean Core Compliance',
      value: `${(metrics?.clean_core_compliance_rate || 0).toFixed(1)}%`,
      icon: FileCheck,
      description: 'Compliance rate',
      color: 'text-teal-600'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Welcome to SAP AI Customization Engine</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {metricCards.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} data-testid={`metric-card-${index}`}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
                <Icon className={`h-4 w-4 ${metric.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{metric.value}</div>
                <p className="text-xs text-muted-foreground">{metric.description}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Platform Overview</CardTitle>
          <CardDescription>SAP AI Customization Engine - Multi-Agent Orchestration Platform</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between pb-2 border-b">
              <span className="font-medium">Average Delivery Time</span>
              <span className="text-green-600 font-semibold">{metrics?.average_delivery_time || 0} days</span>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground mb-1">Clean Core Principles</div>
                <div className="font-medium">All customizations validated</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">AI Agents</div>
                <div className="font-medium">Design, Dev, Test, DevOps, Governance</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Integrations</div>
                <div className="font-medium">Jira, SAP BTP</div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Automation Level</div>
                <div className="font-medium">70% reduction in manual effort</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
